import React, { useCallback, useMemo } from 'react'

import Form, { FormGeneratorHandles } from '@/components/Form'
import Splash from '@/components/splash/Splash'
import Button from '@/components/UiElements/Button'
import useStateManagement from '@/hooks/stateManagement/useStateManagement'

import designTemplateFormItems from './forms/designTemplateFormItems'
import saveDesignTemplateStates from './utils/saveDesignTemplateStates'
import DesignSummary from './components/DesignSummary'
import { DesignTemplateDetail } from '@/types/designTemplate'

export interface SavedTemplateRef {
  id?: string
  sku?: string
  sectionId?: string
}

interface DesignTemplateModalProps {
  currentTemplate?: DesignTemplateDetail | null
  onClose: () => void
  /** Called after a successful save, with enough to open the designer. */
  onSaved: (saved: SavedTemplateRef) => Promise<void> | void
  /** Save, then open the design surface on the saved template. */
  onSaveAndDesign: (saved: SavedTemplateRef) => Promise<void> | void
}

const DesignTemplateModal: React.FC<DesignTemplateModalProps> = ({
  currentTemplate,
  onClose,
  onSaved,
  onSaveAndDesign,
}) => {
  const formRef = React.useRef<FormGeneratorHandles>(null)
  const isNew = !currentTemplate?.id

  const defaultValues = useMemo(() => {
    if (!currentTemplate) {
      return { isActive: true, isGeneric: true }
    }

    const origin = currentTemplate.data?.origin

    return {
      name: currentTemplate.name,
      description: currentTemplate.description,
      isActive: currentTemplate.is_active,
      isGeneric: currentTemplate.is_generic,
      // The product is stored on the design, so an edit reopens on the same one.
      product: origin?.sku ? { value: origin.sku, label: origin.sku } : null,
    }
  }, [currentTemplate])

  const { runSteps, stateInProgress } = useStateManagement({ ...saveDesignTemplateStates })

  const runSave = useCallback(
    (then: (saved: SavedTemplateRef) => Promise<void> | void) => {
      runSteps({
        formRef,
        currentTemplate,
        templateData: currentTemplate?.data,
        onSaved: then,
      })
    },
    [runSteps, currentTemplate]
  )

  return (
    <div className={'flex flex-col gap-xxxl'}>
      {stateInProgress && <Splash />}

      <div className={'w-[560px] grid grid-cols-12 gap-md'}>
        <Form
          key={currentTemplate?.id || 'new'}
          ref={formRef}
          items={designTemplateFormItems}
          defaultValues={defaultValues}
        />
      </div>

      <DesignSummary template={currentTemplate} />

      <div className={'flex flex-row justify-between gap-md'}>
        <Button onClick={onClose} type={'alt'} className={'flex-1 inset-1'}>
          Cancel
        </Button>
        {!isNew && (
          <Button onClick={() => runSave(onSaved)} type={'alt'} className={'flex-1 inset-1'}>
            Save
          </Button>
        )}
        <Button onClick={() => runSave(onSaveAndDesign)} className={'flex-1 inset-1'}>
          {isNew ? 'Create and design' : 'Edit design'}
        </Button>
      </div>
    </div>
  )
}

export default DesignTemplateModal
